import { useCallback, useEffect, useRef, useState } from "react";
import { themeVar as colors, themeTierStyles as tierStyles } from "../design-tokens";
import { measure } from "../ui/measures";
import { ConfirmGrid } from "./ConfirmGrid";
import { cadTypeahead, cotalityLookupSubject } from "./api";
import {
  PUBLIC_RECORDS_UNAVAILABLE, cadRecordTag, changeEntry, fieldLabel, gridFromCadRow,
  handEditedKeys, livingAreaFlagNote, normalizeCadResults,
  didYouMean, rowDetails, zeroState,
} from "./cadPick";
import type { CadPick, CadRow, CadSpelling, CadZeroState, SubjectChangeEntry } from "./cadPick";
import { filledCount, gridFromSubject, isFieldConfirmed, landedFields } from "./subjectGrid";
import type { SubjectGrid } from "./types";
import { useUsage, refreshUsage } from "../lib/useUsage";
import { atUsageLimit, isLimitReached } from "../lib/usageApi";
import { LimitNoticeView, ServerLimitNotice } from "../ui/UsageWidgets";

/**
 * STEP 1, FIRST THING ON THE SCREEN: WHICH HOME ARE WE VALUING? (2026-08-04, owner directive —
 * "the first thing that needs to be done in the process is to enter the subject address, show the
 * details it finds… in a form where the user can select or deselect or even edit the data.")
 *
 * What this replaces. The address was a plain optional text box below the dropzone, described as
 * something you could "leave blank", with no lookup attached to it at all. The public-records fetch
 * existed but lived two screens later behind a button, so the ordinary path was: upload a file,
 * wait out an OCR pass, land on step 2, discover the subject was not in the file, and only THEN be
 * invited to go and find it. The owner's own screenshot is that path's ending — "Subject: no record
 * for 4815 Springtree Lane Wylie TX was found in the upload".
 *
 * The address is the one fact the agent always has, it costs nothing to ask for, and it is what
 * every provider keys on. Asking for it first turns a late dead end into the first thing that
 * works.
 *
 * TWO PIECES OF DISCIPLINE CARRIED OVER UNCHANGED, because this screen is a new place for the same
 * old mistakes to happen:
 *
 *   * NOTHING IS ASSUMED. A field the provider did not return stays blank and unchecked. There is
 *     no "close enough" match and no borrowed value — the same rule that makes the product refuse
 *     to promote a comparable into the subject slot.
 *   * SOURCE-TAGGED, AND NOT YET CONFIRMED. Everything here is labelled with where it came from,
 *     every row can be unticked or corrected, and the provider's response carries
 *     `confirmed: false` for the whole session. A public-record aggregate is a starting point, and
 *     the licensed human is the one who says otherwise.
 *
 * The form is `ConfirmGrid sections="subject"
            // Step 1 is the address lookup; the half-bath caution belongs on step 2, the last
            // subject screen before the comps, where it is the agent's remaining move.
            showBathNote={false}` — the SAME component step 2 renders. Not a
 * lookalike: one component means the checkbox, the source tag, the "edited" mark and the
 * provenance rules cannot drift between the screen that fetches the data and the screen that
 * confirms it.
 */
/** Module-level so the array identity is stable across renders — a fresh `["address"]` on every
 * render would be a new prop object every time for no reason. */
const ADDRESS_ONLY = ["address"];

/** What the agent reads when no county record matched and there is nothing else to ask (the
 * public-records connector is dormant). True in every case it is shown, and names no provider. */
export const DORMANT_NO_COUNTY_NOTE =
  "No county record matched that address. Enter the subject's details below, or upload the " +
  "county/CAD sheet.";
/** The same, when the county search itself could not answer: no claim about a match either way. */
export const DORMANT_ENTER_NOTE =
  "Enter the subject's details below, or upload the county/CAD sheet.";

/** The listbox's id, for `aria-controls`; one picker per screen. */
const LIST_ID = "vb-subject-cad-list";
const optionId = (i: number) => `vb-subject-cad-opt-${i}`;
/** Owner design: 4+ characters, ~250 ms. */
const MIN_CHARS = 4;
const DEBOUNCE_MS = 250;

function isAbort(e: unknown): boolean {
  return !!e && typeof e === "object" && (e as { name?: unknown }).name === "AbortError";
}

export function SubjectAddressStep({
  addressLine,
  onAddressChange,
  grid,
  onGridChange,
  condition,
  onConditionChange,
  busy,
  onClipResolved,
  publicRecordsDormant = false,
  cadPick,
  onCadPickChange,
  onSubjectChangeLogged,
}: {
  addressLine: string;
  onAddressChange: (v: string) => void;
  /** null until a lookup has actually returned something — the form is not rendered before that,
   * because an empty form invites the agent to type the whole record by hand before they have
   * found out that we could have fetched it. */
  grid: SubjectGrid | null;
  onGridChange: (g: SubjectGrid) => void;
  condition: string;
  onConditionChange: (v: string) => void;
  busy?: boolean;
  /** THE RESOLVED RECORD, LIFTED (2026-09-01, T-5).
   *
   * This step resolves a Cotality clip from the lookup's own response when the agent typed an
   * address and pressed Enter (or left the field), and publishes it so
   * `CmaAmbFlow.handleFetchPublicRecords` does not look the SAME subject up again by address.
   *
   * THE ADDRESS TRAVELS WITH THE CLIP, and that is not decoration. A clip identifies a property,
   * not a string, so a clip kept past an edit of the address would fetch the OLD property and
   * present it under the NEW address. `null` here means "there is no resolved record any more",
   * which a typed edit — or a CAD pick, which is a different record — sends immediately. */
  onClipResolved?: (resolved: { clip: string; address: string } | null) => void;
  /** THE COUNTY RECORD THE SUBJECT IS LOCKED TO (2026-10-04), by `prop_id`. Held by the flow so it
   * survives this screen unmounting and is saved with the review. Omitted, the step keeps it
   * locally — the same component, uncontrolled. */
  cadPick?: CadPick | null;
  onCadPickChange?: (pick: CadPick | null) => void;
  /** One entry per confirmed switch of subject record. The flow keeps the ledger, saves it with
   * the review and sends it with Generate; the server stamps who. */
  onSubjectChangeLogged?: (entry: SubjectChangeEntry) => void;
  /** THE PUBLIC-RECORDS CONNECTOR IS DORMANT (owner, 2026-10-20). Then only the county records are
   * asked: the lookup below is skipped, not attempted and refused, and nothing says why — a parked
   * source is not news to the agent. */
  publicRecordsDormant?: boolean;
}) {
  const [clip, setClip] = useState<string | null>(null);
  /** THE ONLY WRITER of `clip`, so the lifted copy in the flow cannot drift from this one. */
  const applyClip = useCallback(
    (next: { clip: string; address: string } | null) => {
      setClip(next?.clip ?? null);
      onClipResolved?.(next);
    },
    [onClipResolved]
  );

  // ---- the locked county record ---------------------------------------------------------------
  const [localPick, setLocalPick] = useState<CadPick | null>(null);
  const pick = cadPick !== undefined ? cadPick : localPick;
  const setPick = (p: CadPick | null) => {
    if (cadPick === undefined) setLocalPick(p);
    onCadPickChange?.(p);
  };
  /** "Change" reopens the picker over a subject that stays chosen until another row is picked. */
  const [changing, setChanging] = useState(false);
  const [changeQuery, setChangeQuery] = useState("");
  const locked = !!pick && !changing;
  const query = changing ? changeQuery : addressLine;

  // ---- the CAD type-ahead state ---------------------------------------------------------------
  const [rows, setRows] = useState<CadRow[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [zero, setZero] = useState<CadZeroState | null>(null);
  /** No match: the county's own nearest street spellings ("Did you mean…"). Never a pick. */
  const [spellings, setSpellings] = useState<CadSpelling[]>([]);
  /** The type-ahead's own `limit_reached` sentence (owner design: shown in place; typing works). */
  const [typeaheadLimit, setTypeaheadLimit] = useState<string | null>(null);
  /** Any other failure. Said quietly, in place — the agent can still type the whole address. */
  const [typeaheadDown, setTypeaheadDown] = useState(false);
  const pastedRef = useRef(false);
  /** A pick waiting on the agent: the switch would replace corrections they made by hand. */
  const [pending, setPending] = useState<{ row: CadRow; keys: string[] } | null>(null);
  /** What the last confirmed switch replaced, said once in words. Never names who. */
  const [switched, setSwitched] = useState<{ from: CadPick; to: CadPick } | null>(null);

  // ---- the Cotality lookup state --------------------------------------------------------------
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  /** A calm, non-alarm line: the public-records lookup is unavailable, which blocks nothing. */
  const [lookupNotice, setLookupNotice] = useState<string | null>(null);
  /** §6: the server's own `limit_reached` sentence, when the lookup this screen just made came
   * back refused — the race case proactive disabling cannot always catch. */
  const [limitRefusal, setLimitRefusal] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** Guards against a slow response for an address the agent has already changed overwriting a
   * fast one — and against a Cotality answer landing on top of a CAD record picked meanwhile. */
  const runRef = useRef(0);
  /** The address the last Cotality lookup was made for, so a blur does not re-spend on it. */
  const lastLookupRef = useRef<string | null>(null);

  const { usage } = useUsage();
  const limited = atUsageLimit(usage);

  // ---- type-ahead: COUNTY CAD ONLY ------------------------------------------------------------
  // Owner design (2026-10-04): no Cotality call while typing. 4+ characters, ~250 ms debounce, the
  // request in flight is ABORTED the moment the text changes (so a slow answer for "100 Ma" can
  // never land over the one for "100 Main"), and a paste fires at once rather than waiting out a
  // debounce meant for keystrokes.
  useEffect(() => {
    const q = query.trim();
    if (locked || q.length < MIN_CHARS) {
      setRows([]);
      setZero(null);
      setSpellings([]);
      setListOpen(false);
      setActive(-1);
      return;
    }
    const delay = pastedRef.current ? 0 : DEBOUNCE_MS;
    pastedRef.current = false;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      // `Promise.resolve().then` so even a synchronous throw lands in the one catch below.
      void Promise.resolve()
        .then(() => cadTypeahead(q, ctrl.signal))
        .then((res) => {
          if (ctrl.signal.aborted) return;
          const next = normalizeCadResults(res);
          setRows(next);
          setActive(-1);
          setZero(next.length ? null : zeroState(res));
          setSpellings(next.length ? [] : didYouMean(res));
          setListOpen(next.length > 0);
          setTypeaheadLimit(null);
          setTypeaheadDown(false);
        })
        .catch((e) => {
          if (ctrl.signal.aborted || isAbort(e)) return;
          setRows([]);
          setListOpen(false);
          setZero(null);
          setSpellings([]);
          if (isLimitReached(e)) {
            setTypeaheadLimit(e.body.message);
            refreshUsage();
            return;
          }
          setTypeaheadDown(true);
        });
    }, delay);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query, locked]);

  // ---- the lookup: COUNTY RECORDS FIRST, then public records ------------------------------------
  // Owner design: fires ONLY on a complete address — signaled by the agent pressing Enter or
  // leaving the field, never guessed from what the text looks like — and ONLY if no CAD row was
  // picked. A picked record is the subject; nothing else is asked about it.
  //
  // CAD FIRST (2026-10-17, owner-reported: "937 field trail mesquite tx" called the public-records
  // provider, which was offline, while the county record held the house). The county records we
  // hold are asked first, through the same type-ahead the list uses:
  //   exactly one match  -> loaded exactly as if the agent picked it (`choose`: same path, same
  //                         source tags, and a correction is never silently replaced);
  //   several matches    -> shown as the pick list; nothing else is called;
  //   none (or no county record for the address) -> the public-records lookup, as before.
  const chooseRef = useRef<(row: CadRow) => void>(() => {});
  const runLookup = useCallback(
    async (address: string, lockedClip: string | null) => {
      if (pick) return;
      const q = address.trim();
      if (!q) {
        setLookupError("Type the subject's address first — there's nothing to look up yet.");
        return;
      }
      const run = ++runRef.current;
      lastLookupRef.current = q;
      setLooking(true);
      setLookupError(null);
      setLookupNotice(null);
      setLimitRefusal(null);
      setNote(null);
      setListOpen(false);
      let countyRows: CadRow[] = [];
      let countyAnswered = true;
      try {
        countyRows = normalizeCadResults(await cadTypeahead(q));
      } catch (e) {
        if (isLimitReached(e)) {
          if (run !== runRef.current) return;
          setLimitRefusal(e.body.message);
          refreshUsage();
          setLooking(false);
          return;
        }
        /* The county search could not answer: the public-records lookup below is still asked. */
        countyAnswered = false;
      }
      if (run !== runRef.current) return;
      if (countyRows.length === 1) {
        setLooking(false);
        chooseRef.current(countyRows[0]);
        return;
      }
      if (countyRows.length > 1) {
        setLooking(false);
        setRows(countyRows);
        setZero(null);
        setSpellings([]);
        setActive(-1);
        setListOpen(true);
        setNote(
          `${countyRows.length} properties in the county records match that address. Pick the ` +
          "one you mean from the list."
        );
        return;
      }
      if (publicRecordsDormant) {
        // Nothing else to ask. The address is kept as typed and the grid is the agent's to fill.
        setLooking(false);
        onGridChange(gridFromSubject({ address: q }, "You entered"));
        setNote(countyAnswered ? DORMANT_NO_COUNTY_NOTE : DORMANT_ENTER_NOTE);
        return;
      }
      try {
        const res = await cotalityLookupSubject(lockedClip ? { clip: lockedClip } : { address: q });
        if (run !== runRef.current) return;         // a newer lookup, or a CAD pick, has happened
        const landed = landedFields(res.data);
        if (!landed.length) {
          setLookupError(
            "We couldn't find a public record for that address. Check the address, or just fill " +
            "the details in below — nothing here depends on the lookup working."
          );
          onGridChange(gridFromSubject({ address: q }, "You entered"));
          return;
        }
        const resolvedAddress = pickAddress(res.data, q);
        if (res.clip) onClipResolved?.({ clip: String(res.clip), address: resolvedAddress || q });
        const next = gridFromSubject(
          { ...(res.data as Record<string, unknown>), address: resolvedAddress },
          res.source_tag || "Cotality public records"
        );
        onGridChange(next);
        setNote(
          `Found ${landed.length} field${landed.length === 1 ? "" : "s"} for this address. ` +
          "Everything below is source-tagged and nothing is confirmed yet — untick anything you " +
          "don't want used, and correct anything that's wrong."
        );
      } catch (e) {
        if (run !== runRef.current) return;
        if (isLimitReached(e)) {
          setLookupError(null);
          setLookupNotice(null);
          setLimitRefusal(e.body.message);
          refreshUsage();
          return;
        }
        // Calm, and the provider's own message is not repeated: it names the vendor and says "CAD
        // data is needed", which is false whenever a county record exists (C, 2026-10-17).
        setLookupNotice(PUBLIC_RECORDS_UNAVAILABLE);
        onGridChange(gridFromSubject({ address: q }, "You entered"));
      } finally {
        if (run === runRef.current) setLooking(false);
      }
    },
    [pick, onGridChange, onClipResolved, publicRecordsDormant]
  );

  /** Leaving the field is the agent saying the address is complete. Not re-spent on an address
   * already looked up, not while a lookup is running, and not at the report limit. */
  function lookupOnBlur() {
    if (pick || changing || looking || busy || limited) return;
    const q = addressLine.trim();
    if (!q || q === lastLookupRef.current) return;
    void runLookup(q, clip);
  }

  /** "Did you mean…": put the county's spelling in the field and search again. Never a pick. */
  function followSpelling(s: CadSpelling) {
    setPending(null);
    setSpellings([]);
    if (changing) {
      setChangeQuery(s.query);
      return;
    }
    onAddressChange(s.query);
    applyClip(null);            // a different address invalidates a previously resolved record
  }

  // ---- picking a county record ----------------------------------------------------------------
  function choose(row: CadRow) {
    setListOpen(false);
    setActive(-1);
    const keys = handEditedKeys(grid);
    if (keys.length) {
      // NEVER SILENTLY DISCARD A CORRECTION. Name what will be replaced and wait for the agent.
      setPending({ row, keys });
      return;
    }
    commit(row, []);
  }

  function commit(row: CadRow, replacedKeys: string[]) {
    const prev = pick;
    runRef.current++;                    // a Cotality answer still in flight must not land on this
    if (prev || replacedKeys.length) {
      onSubjectChangeLogged?.(changeEntry(prev, row.pick, grid, replacedKeys));
    }
    const next = gridFromCadRow(row);
    onGridChange(next);
    onAddressChange(row.pick.address);
    applyClip(null);                     // a Cotality clip names a different record
    setPick(row.pick);
    setSwitched(prev && prev.propId !== row.pick.propId ? { from: prev, to: row.pick } : null);
    setChanging(false);
    setChangeQuery("");
    setPending(null);
    setRows([]);
    setZero(null);
    setLookupError(null);
    setLookupNotice(null);
    setLimitRefusal(null);
    const n = filledCount(next);
    const flagNote = livingAreaFlagNote(row.raw.living_area_flagged);
    // Rows prefilled from a reading the agent must confirm stay unchecked (`gridFromCadRow`).
    const r = Object.values(next).filter((f) => f && f.value !== null && !f.checked).length;
    const reviewNote = r
      ? ` ${r} of them need${r === 1 ? "s" : ""} your review and ${r === 1 ? "is" : "are"} not used until you check ${r === 1 ? "it" : "them"}.`
      : "";
    setNote(
      `Filled ${n} field${n === 1 ? "" : "s"} from the ${row.pick.cadLabel} record. Each one is ` +
      "source-tagged and nothing is confirmed yet — untick anything you don't want used, and " +
      "correct anything that's wrong." + reviewNote + (flagNote ? ` ${flagNote}` : "")
    );
  }

  chooseRef.current = choose;

  function startChange() {
    setChanging(true);
    setChangeQuery("");
    setSwitched(null);
    setNote(null);
  }

  function cancelChange() {
    setChanging(false);
    setChangeQuery("");
    setPending(null);
    setRows([]);
    setZero(null);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!rows.length) return;
      e.preventDefault();
      if (!listOpen) {
        setListOpen(true);
        setActive(e.key === "ArrowDown" ? 0 : rows.length - 1);
        return;
      }
      setActive((i) =>
        e.key === "ArrowDown" ? (i + 1) % rows.length : i <= 0 ? rows.length - 1 : i - 1
      );
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (listOpen && active >= 0 && rows[active]) {
        choose(rows[active]);
        return;
      }
      if (!pick && !changing && !limited) void runLookup(addressLine, clip);
      return;
    }
    if (e.key === "Escape") {
      if (listOpen) setListOpen(false);
      else if (changing) cancelChange();
    }
  }

  const found = filledCount(grid);
  /** THE ADDRESS IS ASKED FOR ONCE (2026-08-05). The confirmed value is PRINTED rather than asked
   * again as row 1 of the grid. Suppressed only when it is genuinely confirmed. */
  const addressConfirmed = isFieldConfirmed(grid, "address");
  const confirmedAddress = addressConfirmed ? String(grid?.address?.value ?? "") : "";
  const showList = listOpen && rows.length > 0 && !locked;
  const q = query.trim();

  return (
    <section style={{ marginTop: 4 }}>
      <h2 className="vb-sr-only">Which home are you valuing?</h2>
      <p style={{ color: colors.textSecondary, marginTop: 6, marginBottom: 10, fontSize: 13, lineHeight: 1.55, maxWidth: measure.copy }}>
        Start with the address. We&apos;ll pull whatever the public record holds and show you all of
        it, so you can check it before anything is calculated.
      </p>

      {locked && pick ? (
        /* THE CHOSEN RECORD STAYS ON SCREEN, WITH ITS WAY OUT BESIDE IT (owner design, 2026-10-04).
           Not an input in an edit mode: the subject is a decision, shown as one, and "Change" is
           the one control that reopens it. */
        <div
          data-testid="cad-subject-locked"
          className="field"
          style={{ maxWidth: measure.prose, display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}
        >
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 11.5, color: colors.textSecondary }}>Subject address</p>
            <p style={{ margin: "4px 0 0", fontSize: 14.5, fontWeight: 550, color: colors.textPrimary }}>
              {pick.address}
            </p>
            {pick.details && (
              <p data-testid="cad-subject-details"
                 style={{ margin: "2px 0 0", fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.5 }}>
                {pick.details}
              </p>
            )}
            <p style={{ margin: "4px 0 0", fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
              <span style={{ color: tierStyles.verified.fg, fontWeight: 600 }}>
                {tierStyles.verified.label}
              </span>
              {" "}· {cadRecordTag(pick)}
            </p>
          </div>
          <button
            type="button"
            className="btn"
            onClick={startChange}
            aria-label={`Change the subject property (now ${pick.address})`}
          >
            Change
          </button>
        </div>
      ) : (
        <div className="field" style={{ position: "relative", maxWidth: measure.prose }}>
          <label htmlFor="vb-subject-address">
            Subject address
          </label>
          <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            <input
              id="vb-subject-address"
              type="text"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={showList}
              aria-controls={LIST_ID}
              aria-activedescendant={showList && active >= 0 ? optionId(active) : undefined}
              value={query}
              autoComplete="off"
              onPaste={() => { pastedRef.current = true; }}
              onChange={(e) => {
                setPending(null);
                if (changing) {
                  setChangeQuery(e.target.value);
                  return;
                }
                onAddressChange(e.target.value);
                applyClip(null);        // a typed edit invalidates a previously resolved record
              }}
              onKeyDown={onKeyDown}
              onBlur={() => {
                setTimeout(() => setListOpen(false), 150);
                lookupOnBlur();
              }}
              aria-describedby="vb-subject-address-help"
              placeholder={changing ? "Search county records for another address" : "123 Main St, Plano, TX"}
              className="box"
              style={{ flex: "1 1 320px" }}
            />
            {changing && pick ? (
              /* While changing, the old record is still the subject: Cotality is never asked, so
                 the Look-up button is not offered. The one thing to do instead is keep it. */
              <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={cancelChange}>
                Keep {pick.address}
              </button>
            ) : (
              <button
                type="button"
                // No blur on the way to this button: the blur would start the same lookup the
                // click is about to start.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void runLookup(addressLine, clip)}
                disabled={looking || busy || limited || addressLine.trim().length === 0}
                className="btn"
              >
                {looking ? "Looking it up…" : "Look up this property"}
              </button>
            )}
          </div>
          {limited && usage && !looking && !busy && addressLine.trim().length > 0 && !changing && (
            <LimitNoticeView usage={usage} />
          )}
          {limitRefusal && <ServerLimitNotice message={limitRefusal} />}
          {/* The type-ahead's own refusal at the report limit, in place. Typing still works. */}
          {typeaheadLimit && <ServerLimitNotice message={typeaheadLimit} />}

          {showList && (
            <ul
              id={LIST_ID}
              role="listbox"
              aria-label="County records matching this address"
              style={{
                listStyle: "none", margin: "4px 0 0", padding: 0, position: "absolute", zIndex: 20,
                left: 0, right: 0, background: colors.bgSurface, border: `1px solid ${colors.border}`,
                borderRadius: 8, maxHeight: 320, overflowY: "auto",
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",
              }}
            >
              {rows.map((row, i) => (
                <li
                  key={row.pick.propId}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === active}
                  data-match={typeof row.raw.match === "string" ? row.raw.match : undefined}
                  // `onMouseDown` keeps focus in the input, so its blur (and the Cotality lookup a
                  // blur means) never fires on the way to a pick. The pick itself is the click.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(row)}
                  style={{
                    padding: "8px 10px", cursor: "pointer", fontSize: 13,
                    color: colors.textPrimary,
                    background: i === active ? colors.bgApp : "transparent",
                  }}
                >
                  <span style={{ display: "block" }}>{row.pick.address}</span>
                  <span style={{ display: "block", fontSize: 12, color: colors.textSecondary }}>
                    {rowDetails(row)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!locked && zero && q.length >= MIN_CHARS && !typeaheadLimit && (
        /* ZERO MATCHES IS A DESIGNED STATE, AND THERE ARE TWO (owner design). "We hold that county
           and the address isn't in it" is a finding; "we hold no dataset for that ZIP" is the
           county record never having been consulted. The server's own sentence is printed. */
        <p
          data-testid="cad-zero"
          data-state={zero.kind}
          style={{ marginTop: 6, fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.5, maxWidth: measure.copy }}
        >
          {zero.kind === "not_in_dataset" && (
            <strong style={{ color: colors.textPrimary }}>
              Not in the county record we hold.{" "}
            </strong>
          )}
          {zero.kind === "no_dataset" && (
            <strong style={{ color: colors.textPrimary }}>No county records here yet. </strong>
          )}
          {zero.reason}{" "}
          {!pick && "Finish the address and press Enter to search public records instead."}
        </p>
      )}
      {!locked && zero && spellings.length > 0 && q.length >= MIN_CHARS && !typeaheadLimit && (
        /* NOTHING MATCHED, SO THE NEAREST COUNTY SPELLINGS (owner spec §4). Each is a real street
           in the county record with a property at the typed house number. Choosing one only puts
           that spelling in the field and searches again: the agent still picks the record. */
        <div data-testid="cad-did-you-mean" style={{ marginTop: 6, fontSize: 12.5, maxWidth: measure.copy }}>
          <span style={{ color: colors.textSecondary }}>Did you mean…</span>
          <ul style={{ listStyle: "none", margin: "4px 0 0", padding: 0, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {spellings.map((s) => (
              <li key={`${s.county ?? ""}|${s.query}`}>
                <button
                  type="button"
                  className="btn"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => followSpelling(s)}
                  style={{ fontSize: 12.5 }}
                >
                  {s.query}
                  {s.county && (
                    <span style={{ color: colors.textSecondary }}> · {s.county} County</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!locked && typeaheadDown && q.length >= MIN_CHARS && (
        <p style={{ marginTop: 6, fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.5 }}>
          County suggestions aren&apos;t available right now.{" "}
          {!pick && "Type the full address and press Enter to look it up."}
        </p>
      )}

      {pending && (
        <div
          data-testid="cad-switch-warning"
          role="alert"
          style={{
            marginTop: 10, padding: "10px 12px", borderRadius: 8, maxWidth: measure.copy,
            border: `1px solid ${colors.caution}`, background: colors.bgSurface, fontSize: 13, lineHeight: 1.5,
          }}
        >
          <p style={{ margin: 0, color: colors.textPrimary }}>
            Switching to <strong>{pending.row.pick.address}</strong> will replace what you entered
            for {joinLabels(pending.keys)} with that record&apos;s figures. Your entries stay on record
            in this review&apos;s change history.
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn" onClick={() => commit(pending.row, pending.keys)}>
              Replace with this record
            </button>
            <button type="button" className="btn" onClick={() => { setPending(null); if (pick) cancelChange(); }}>
              Keep the current subject
            </button>
          </div>
        </div>
      )}

      {!pick && (
        <p id="vb-subject-address-help" style={{ marginTop: 6, fontSize: 12, color: colors.textSecondary, lineHeight: 1.5, maxWidth: measure.copy }}>
          The property you&apos;re valuing. Start typing and pick it from the county records listed
          below, or type it in full and press Enter to look it up. You can also skip the lookup
          entirely and fill the details in yourself — nothing below depends on it.
        </p>
      )}
      {pick && changing && (
        <p id="vb-subject-address-help" style={{ marginTop: 6, fontSize: 12, color: colors.textSecondary, lineHeight: 1.5, maxWidth: measure.copy }}>
          Pick another county record. {pick.address} stays the subject until you do.
        </p>
      )}

      <div role="status" style={{ minHeight: 0 }}>
        {looking && (
          <p style={{ marginTop: 8, fontSize: 13, color: colors.textSecondary }}>
            Searching the public record for {addressLine.trim()}…
          </p>
        )}
        {switched && !looking && (
          <p style={{ marginTop: 8, fontSize: 13, color: colors.textPrimary, lineHeight: 1.5 }}>
            Subject changed from {switched.from.address} (record {switched.from.propId}) to{" "}
            {switched.to.address}. The change is kept in this review&apos;s history.
          </p>
        )}
        {note && !looking && (
          <p style={{ marginTop: 8, fontSize: 13, color: colors.verified, lineHeight: 1.5 }}>{note}</p>
        )}
      </div>
      {lookupError && (
        <p role="alert" style={{ marginTop: 8, fontSize: 13, color: colors.caution, lineHeight: 1.5 }}>
          {lookupError}
        </p>
      )}
      {lookupNotice && (
        <p role="status" style={{ marginTop: 8, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
          {lookupNotice}
        </p>
      )}

      {grid && (
        <div style={{ marginTop: 16, paddingTop: 4, borderTop: `1px solid ${colors.border}` }}>
          {addressConfirmed && !pick && (
            <p style={{ fontSize: 13, color: colors.textPrimary, margin: "12px 0 0", lineHeight: 1.55 }}>
              <strong>{confirmedAddress}</strong>
              <span style={{ color: colors.textSecondary, fontSize: 12 }}>
                {" "}· {grid.address?.sourceTag ?? "You entered"}. Change it in the box above.
              </span>
            </p>
          )}
          <p style={{ fontSize: 12, color: colors.textSecondary, margin: "12px 0 4px", lineHeight: 1.55, maxWidth: measure.copy }}>
            <strong style={{ color: colors.textPrimary }}>
              {found} detail{found === 1 ? "" : "s"} on this property.
            </strong>{" "}
            Every row is ticked only because a source stated it. Untick anything you don&apos;t want
            used, correct anything that&apos;s wrong — a corrected figure keeps the original on
            record — and fill in anything blank. A blank stays blank; we never assume a value.
          </p>
          <ConfirmGrid
            grid={grid}
            onChange={onGridChange}
            condition={condition}
            onConditionChange={onConditionChange}
            comps={[]}
            onCompsChange={() => {}}
            addressLine={confirmedAddress || addressLine}
            sections="subject"
            hideKeys={addressConfirmed ? ADDRESS_ONLY : undefined}
            cadPick={pick}
            // A BLANK ROW SAYS WHY, HERE TOO (2026-10-19, #40 x #35). The picked record's own
            // why-blank sentence (`field_notes`) was wired only into the review grid, so on this
            // step a blank Lot size read "Nothing on record" beside the empty field. Same source,
            // same sentence as the review step; no note, no change.
            blankNote={pick?.fieldNotes ? (key) => pick.fieldNotes?.[key] : undefined}
          />
        </div>
      )}
    </section>
  );
}

/** "Living area (GLA)", "Living area (GLA) and Lot size", "A, B and C". */
function joinLabels(keys: string[]): string {
  const labels = keys.map(fieldLabel);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

// ------------------------------------------------------------------------------------------------
// provider response shapes — normalized here so nothing above has to know them
// ------------------------------------------------------------------------------------------------

function firstString(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

/** Prefer the address the RECORD states over the one that was typed — the provider's is the
 * normalized, deliverable form. Falls back to what the agent typed when the record has none, so
 * the subject is never left without an address it can be identified by. */
function pickAddress(data: unknown, typed: string): string {
  if (data && typeof data === "object") {
    const a = firstString(data as Record<string, unknown>, ["address", "fullAddress", "formattedAddress"]);
    if (a) return a;
  }
  return typed;
}
